// Physics Workbench - deterministic freeform spec parser. NO AI.
//
// Maps a freeform `path = value` spec onto an engine's OWN parameters. The
// `defaults` object is fetched live from the engine's get_*_defaults() wasm
// export (the single source of truth), so this layer never invents field names
// or types: it only OVERRIDES leaves that already exist. Discipline mirrors the
// dynsys parser - whitelist-by-defaults, error on any unknown path, coerce the
// value to the existing leaf's type. Pure + side-effect-free so it is unit
// testable under node and identical in the browser.
//
// Spec grammar (one override per line):
//   path = value           e.g.  radial_points = 200
//   a.b.c = value          dot path into nested objects
//   arr.0.field = value    array index as a dot segment, or arr[0].field
//   # comment   // comment  blank lines ignored
// value coercion follows the EXISTING leaf's type:
//   number  -> parseFloat (supports 5e10); error if NaN
//   boolean -> true/false (case-insensitive)
//   string  -> taken verbatim
//   array/object -> JSON.parse(value) (must parse)

function parsePath(p) {
  // Normalize a[0].b -> a.0.b, then split on dots. Reject empties.
  const norm = p.replace(/\[(\d+)\]/g, '.$1');
  const segs = norm.split('.').map(s => s.trim());
  if (segs.some(s => s.length === 0)) return null;
  return segs;
}

function coerce(existing, raw) {
  const t = typeof existing;
  if (Array.isArray(existing) || (existing !== null && t === 'object')) {
    try { return { value: JSON.parse(raw) }; }
    catch { return { err: `expected JSON for an array/object field` }; }
  }
  if (t === 'number') {
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return { err: `expected a number, got '${raw}'` };
    return { value: n };
  }
  if (t === 'boolean') {
    const v = raw.toLowerCase();
    if (v === 'true') return { value: true };
    if (v === 'false') return { value: false };
    return { err: `expected true/false, got '${raw}'` };
  }
  // string (or null leaf treated as string-assignable)
  return { value: raw };
}

/// Apply a freeform spec onto a deep copy of `defaults`. Returns
/// { value, errors }: `value` is the merged request object; `errors` lists
/// per-line problems (unknown path, type mismatch). An unknown path is never
/// silently created - the engine's defaults are the authoritative whitelist.
export function applySpec(defaults, specText) {
  const errors = [];
  const out = (typeof structuredClone === 'function')
    ? structuredClone(defaults)
    : JSON.parse(JSON.stringify(defaults));
  const lines = String(specText == null ? '' : specText).split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('#') || line.startsWith('//')) continue;
    const eq = line.indexOf('=');
    if (eq < 0) { errors.push(`line ${i + 1}: expected 'path = value'`); continue; }
    const path = line.slice(0, eq).trim();
    const rawVal = line.slice(eq + 1).trim();
    const segs = parsePath(path);
    if (!segs) { errors.push(`line ${i + 1}: malformed path '${path}'`); continue; }

    // Walk to the parent of the final segment; every intermediate must exist.
    let node = out;
    let ok = true;
    for (let s = 0; s < segs.length - 1; s++) {
      const key = segs[s];
      if (node == null || typeof node !== 'object' || !(key in node)) { ok = false; break; }
      node = node[key];
    }
    const leaf = segs[segs.length - 1];
    if (!ok || node == null || typeof node !== 'object' || !(leaf in node)) {
      errors.push(`line ${i + 1}: unknown path '${path}' (not a parameter of this engine)`);
      continue;
    }
    const c = coerce(node[leaf], rawVal);
    if (c.err) { errors.push(`line ${i + 1}: ${c.err} for '${path}'`); continue; }
    node[leaf] = c.value;
  }
  return { value: out, errors };
}

/// Slice an engine's grouped get_*_defaults() bundle down to the single-engine
/// request shape using a data-driven `requestPath` from the manifest:
///   undefined  -> the bundle already IS the request (return as-is)
///   "a.b"      -> return bundle.a.b
///   {pick:[..]} -> return an object with only those top-level keys
/// Returns null if the path doesn't resolve (caller falls back to PRESETS).
export function sliceRequest(bundle, requestPath) {
  if (requestPath == null) return bundle;
  if (typeof requestPath === 'object' && Array.isArray(requestPath.pick)) {
    const o = {};
    for (const k of requestPath.pick) if (k in (bundle || {})) o[k] = bundle[k];
    return Object.keys(o).length ? o : null;
  }
  if (typeof requestPath === 'string') {
    let node = bundle;
    for (const seg of requestPath.replace(/\[(\d+)\]/g, '.$1').split('.')) {
      if (node == null || typeof node !== 'object' || !(seg in node)) return null;
      node = node[seg];
    }
    return node;
  }
  return null;
}
